"use client";

import * as React from "react";
import {
  DndContext,
  useDraggable,
  useSensor,
  useSensors,
  PointerSensor,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { Maximize2, Minimize2, X } from "lucide-react";

import { Card, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  createPositionStorage,
  createDimensionsStorage,
} from "@/lib/window-storage";
import { useResizableDimensions } from "@/lib/useResizableDimensions";
import { ResizeHandles } from "@/components/ui/widget/resize-handles";
import { type DragHandleProps } from "@/components/ui/widget/widget-overlay-context";

/** Margin from viewport edges used for clamping positions. */
const VIEWPORT_MARGIN = 24;

/** Default dimension constants. */
const DEFAULT_DEFAULT_WIDTH = 420;
const DEFAULT_DEFAULT_HEIGHT = 600;
const DEFAULT_FULLSCREEN_WIDTH = 1200;
const DEFAULT_FULLSCREEN_HEIGHT = 700;

/**
 * Converts a screen Y delta to bottom-positioned Y delta.
 * Since the window uses CSS `bottom` positioning, Y movements are inverted.
 */
function toBottomPositionDelta({ screenDeltaY }: { screenDeltaY: number }): number {
  return -screenDeltaY;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Value exposed by WidgetWindowContext to children. */
type WidgetWindowContextValue = {
  isVisible: boolean;
  isFullScreen: boolean;
  close: () => void;
  toggleFullScreen: (next?: boolean) => void;
  isDragging: boolean;
  isResizing: boolean;
  dragHandleProps: DragHandleProps;
};

/** Props accepted by WidgetWindow.Provider. */
type WidgetWindowProviderProps = {
  children: React.ReactNode;
  storageKeyPrefix: string;
  defaultWidth?: number;
  defaultHeight?: number;
  fullscreenWidth?: number;
  fullscreenHeight?: number;
  isVisible: boolean;
  initialFullScreen?: boolean;
  onClose: () => void;
  onFullScreenChange?: (isFullScreen: boolean) => void;
  onVisibilityChange?: (visible: boolean) => void;
  dndId: string;
  frameTestId?: string;
  isLeft?: boolean;
  isMobile?: boolean;
  onClickOutside?: () => void;
  className?: string;
  frameZIndex?: number;
  overlayZIndex?: number;
  /** Optional initial position override. Used to place the window beside another element. */
  initialPosition?: { x: number; y: number };
  /** Optional callback to resolve custom position when toggling fullscreen. Returns {x, y} with optional width/height overrides, or null to use default. */
  resolveTogglePosition?: (params: {
    entering: boolean;
    width: number;
    height: number;
  }) => { x: number; y: number; width?: number; height?: number } | null;
  /**
   * When true, children stay mounted while hidden (visually hidden via
   * `inert` + opacity/pointer-events instead of unmounting), so their
   * internal state survives a close/reopen cycle. Defaults to false —
   * unmounting on hide is the safe default for consumers whose children
   * hold external resources (e.g. a PDF viewer's pdf.js document, canvases,
   * blob URLs) that should not outlive the window being closed.
   */
  keepMountedWhenHidden?: boolean;
};

/** Props accepted by WidgetWindow.Header. */
type WidgetWindowHeaderProps = {
  children: React.ReactNode;
  className?: string;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * React context providing window state and interactions to child components.
 *
 * Must be consumed within a WidgetWindow.Provider.
 */
const WidgetWindowContext = React.createContext<WidgetWindowContextValue | null>(null);

/**
 * Returns the current WidgetWindowContext value.
 *
 * Throws if called outside a WidgetWindow.Provider.
 */
export function useWidgetWindow(): WidgetWindowContextValue {
  const ctx = React.useContext(WidgetWindowContext);
  if (!ctx) {
    throw new Error("useWidgetWindow must be used within a WidgetWindow.Provider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Internal types passed from outer Provider to inner Frame
// ---------------------------------------------------------------------------

type WindowInternals = {
  position: { x: number; y: number };
  resizableWidth: number;
  resizableHeight: number;
  isVisible: boolean;
  isFullScreen: boolean;
  isDragging: boolean;
  isResizing: boolean;
  positionAdjustment: { deltaX: number; deltaY: number };
  dndId: string;
  frameTestId: string;
  isMobile: boolean;
  close: () => void;
  toggleFullScreen: (next?: boolean) => void;
  startResize: (edge: import("@/lib/useResizableDimensions").ResizeEdge, event: React.PointerEvent) => void;
  className?: string;
  onClickOutside?: () => void;
  frameZIndex?: number;
  overlayZIndex?: number;
};

/**
 * Internal context to pass data from outer Provider (which sets up DndContext)
 * to the inner draggable component (which calls useDraggable).
 */
const WindowInternalsContext = React.createContext<WindowInternals | null>(null);

// ---------------------------------------------------------------------------
// Inner draggable component (must be inside DndContext)
// ---------------------------------------------------------------------------

/**
 * Inner component that calls useDraggable (requires DndContext ancestor).
 *
 * Renders the fixed-positioned frame, resize handles, Card shell, and provides
 * the WidgetWindowContext with drag handle props to children.
 */
function WindowFrameInner({ children }: { children: React.ReactNode }) {
  const internals = React.useContext(WindowInternalsContext)!;

  const {
    position,
    resizableWidth,
    resizableHeight,
    isVisible,
    isFullScreen,
    isDragging,
    isResizing,
    positionAdjustment,
    dndId,
    frameTestId,
    isMobile,
    close,
    toggleFullScreen,
    startResize,
    className,
    onClickOutside,
    frameZIndex,
    overlayZIndex,
  } = internals;

  const { setNodeRef, setActivatorNodeRef, listeners, transform } = useDraggable({
    id: dndId,
  });

  const baseX = position.x + positionAdjustment.deltaX;
  const baseY = position.y + positionAdjustment.deltaY;

  const dragTransform = transform
    ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
    : undefined;

  const dragHandleProps = React.useMemo<DragHandleProps>(
    () => ({ ref: setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  const contextValue = React.useMemo<WidgetWindowContextValue>(
    () => ({
      isVisible,
      isFullScreen,
      close,
      toggleFullScreen,
      isDragging,
      isResizing,
      dragHandleProps,
    }),
    [isVisible, isFullScreen, close, toggleFullScreen, isDragging, isResizing, dragHandleProps]
  );

  return (
    <WidgetWindowContext.Provider value={contextValue}>
      {/* Invisible overlay for click-outside detection (no dark background) */}
      {onClickOutside && (isVisible || isFullScreen) && (
        <div
          role="presentation"
          data-testid="widget-window-overlay"
          onClick={onClickOutside}
          className={cn(
            "fixed inset-0 z-40",
            isFullScreen ? "pointer-events-none" : "pointer-events-auto"
          )}
          style={overlayZIndex ? { zIndex: overlayZIndex } : undefined}
        />
      )}

      <div
        ref={setNodeRef}
        // `inert` (Chrome 102+/Safari 15.5+/Firefox 112+): on an older
        // browser this attribute is simply ignored — the opacity/
        // pointer-events classes below still hide the frame visually and
        // block the mouse, so the only gap is keyboard/screen-reader users
        // on an unsupported browser retaining access to a visually hidden
        // window. No polyfill added; revisit if that gap matters in practice.
        inert={!(isVisible || isFullScreen)}
        aria-hidden={!(isVisible || isFullScreen)}
        data-testid={frameTestId}
        data-dnd-id={dndId}
        className={cn(
          "fixed z-50",
          !isDragging && !isResizing && "transition-all duration-200",
          isVisible || isFullScreen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0 translate-y-4",
          isMobile && "bg-background"
        )}
        style={{
          ...(frameZIndex ? { zIndex: frameZIndex } : {}),
          ...(isMobile
            ? {
                left: 0,
                bottom: 0,
                right: 0,
                top: 0,
                width: "100vw",
                height: "100dvh",
                maxWidth: "100vw",
                paddingTop: "env(safe-area-inset-top)",
                paddingBottom: "env(safe-area-inset-bottom)",
                paddingLeft: "env(safe-area-inset-left)",
                paddingRight: "env(safe-area-inset-right)",
              }
            : {
                left: baseX,
                bottom: baseY,
                right: "auto",
                width: resizableWidth,
                height: resizableHeight,
                maxWidth: "calc(100vw - 3rem)",
                transform: dragTransform,
              }),
        }}
      >
        <div className="relative w-full h-full">
          <ResizeHandles onResizeStart={startResize} disabled={isMobile} />
          <Card
            data-testid="widget-window-card"
            className={cn(
              "w-full h-full rounded-lg shadow-2xl border-none overflow-hidden !py-0 pt-6 !gap-0 flex flex-col",
              className
            )}
            style={{
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            }}
          >
            {children}
          </Card>
        </div>
      </div>
    </WidgetWindowContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Main wrapper component for the floating window abstraction.
 *
 * Manages position and dimensions persistence, drag-and-drop via dnd-kit,
 * resize handles, fullscreen toggling, and click-outside detection.
 * Renders a fixed-positioned frame with a shadcn Card as the visual shell.
 *
 * By default, unmounts children for real when hidden (`isVisible` and
 * `isFullScreen` both false) — safe for consumers whose children hold
 * external resources that should not outlive the window closing (e.g.
 * PdfWindow's pdf.js document, canvases, blob URL).
 *
 * Pass `keepMountedWhenHidden` to opt into the opposite contract instead:
 * children stay mounted for the whole lifetime of this provider, hidden via
 * `inert` + opacity/pointer-events on the frame rather than unmounting, so
 * their internal state (scroll position, in-flight message state) survives
 * a close/reopen cycle. Only use this for children known not to hold
 * resources that need to be released on close.
 */
function WidgetWindowProvider({
  children,
  storageKeyPrefix,
  defaultWidth = DEFAULT_DEFAULT_WIDTH,
  defaultHeight = DEFAULT_DEFAULT_HEIGHT,
  fullscreenWidth = DEFAULT_FULLSCREEN_WIDTH,
  fullscreenHeight = DEFAULT_FULLSCREEN_HEIGHT,
  isVisible,
  initialFullScreen = false,
  onClose,
  onFullScreenChange,
  onVisibilityChange,
  dndId,
  frameTestId = "widget-window-frame",
  isLeft = false,
  isMobile = false,
  onClickOutside,
  className,
  frameZIndex,
  overlayZIndex,
  initialPosition,
  resolveTogglePosition,
  keepMountedWhenHidden = false,
}: WidgetWindowProviderProps) {
  // Create storage instances once, keyed by prefix
  const positionStorage = React.useMemo(
    () => createPositionStorage({ key: `${storageKeyPrefix}_position` }),
    [storageKeyPrefix]
  );
  const dimensionsStorage = React.useMemo(
    () => createDimensionsStorage({ key: `${storageKeyPrefix}_dimensions` }),
    [storageKeyPrefix]
  );

  // Fullscreen state
  const [isFullScreen, setIsFullScreen] = React.useState(initialFullScreen);

  // Position state: initialPosition > stored > default (bottom-right)
  const [position, setPosition] = React.useState<{ x: number; y: number }>(() => {
    if (initialPosition) return initialPosition;
    const stored = positionStorage.get();
    const raw = stored ?? {
      x: window.innerWidth - defaultWidth - VIEWPORT_MARGIN,
      y: VIEWPORT_MARGIN,
    };
    // Clamp to current viewport using stored/default dimensions
    const storedDims = dimensionsStorage.get();
    const w = storedDims?.width ?? defaultWidth;
    const h = storedDims?.height ?? defaultHeight;
    return {
      x: Math.max(VIEWPORT_MARGIN, Math.min(raw.x, window.innerWidth - w - VIEWPORT_MARGIN)),
      y: Math.max(VIEWPORT_MARGIN, Math.min(raw.y, window.innerHeight - h - VIEWPORT_MARGIN)),
    };
  });

  const [isDragging, setIsDragging] = React.useState(false);

  // Resizable dimensions with custom storage
  const {
    width: resizableWidth,
    height: resizableHeight,
    isResizing,
    positionAdjustment,
    startResize,
    setDimensions,
    clearPositionAdjustment,
  } = useResizableDimensions({
    defaultWidth,
    defaultHeight,
    isLeft,
    enabled: true,
    getDimensions: dimensionsStorage.get,
    saveDimensions: dimensionsStorage.save,
  });

  // Clamp position to viewport
  const clampPosition = React.useCallback(
    ({ x, y }: { x: number; y: number }) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const minX = VIEWPORT_MARGIN;
      const maxX = viewportWidth - resizableWidth - VIEWPORT_MARGIN;
      const minY = VIEWPORT_MARGIN;
      const maxY = viewportHeight - resizableHeight - VIEWPORT_MARGIN;

      return {
        x: Math.max(minX, Math.min(maxX, x)),
        y: Math.max(minY, Math.min(maxY, y)),
      };
    },
    [resizableWidth, resizableHeight]
  );

  // Clamp dimensions and position to viewport on mount and resize
  React.useEffect(() => {
    const clampToViewport = () => {
      const maxW = window.innerWidth - 2 * VIEWPORT_MARGIN;
      const maxH = window.innerHeight - 2 * VIEWPORT_MARGIN;
      const clampedW = Math.max(200, Math.min(resizableWidth, maxW));
      const clampedH = Math.max(200, Math.min(resizableHeight, maxH));

      if (clampedW !== resizableWidth || clampedH !== resizableHeight) {
        setDimensions({ width: clampedW, height: clampedH });
      }

      setPosition((prev) => ({
        x: Math.max(VIEWPORT_MARGIN, Math.min(prev.x, window.innerWidth - clampedW - VIEWPORT_MARGIN)),
        y: Math.max(VIEWPORT_MARGIN, Math.min(prev.y, window.innerHeight - clampedH - VIEWPORT_MARGIN)),
      }));
    };

    // Run on mount to handle stale stored dimensions
    clampToViewport();

    let rafId: number | null = null;
    const handleResize = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        clampToViewport();
        rafId = null;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [resizableWidth, resizableHeight, setDimensions]);

  React.useEffect(() => {
    onFullScreenChange?.(isFullScreen);
  }, [isFullScreen, onFullScreenChange]);

  /** Toggles fullscreen mode, resetting dimensions and position accordingly. */
  const toggleFullScreen = React.useCallback((explicitNext?: boolean) => {
    const next = explicitNext ?? !isFullScreen;
    setIsFullScreen(next);

    const targetWidth = next ? fullscreenWidth : defaultWidth;
    const targetHeight = next ? fullscreenHeight : defaultHeight;

    const customPos = resolveTogglePosition?.({
      entering: next,
      width: targetWidth,
      height: targetHeight,
    });

    const nextWidth = customPos?.width ?? targetWidth;
    const nextHeight = customPos?.height ?? targetHeight;
    setDimensions({ width: nextWidth, height: nextHeight });

    const rawPos = customPos
      ? { x: customPos.x, y: customPos.y }
      : { x: window.innerWidth - nextWidth - VIEWPORT_MARGIN, y: VIEWPORT_MARGIN };
    const clampedPos = {
      x: Math.max(VIEWPORT_MARGIN, Math.min(rawPos.x, window.innerWidth - nextWidth - VIEWPORT_MARGIN)),
      y: Math.max(VIEWPORT_MARGIN, Math.min(rawPos.y, window.innerHeight - nextHeight - VIEWPORT_MARGIN)),
    };
    setPosition(clampedPos);
    positionStorage.save(clampedPos);

    onVisibilityChange?.(true);
  }, [
    isFullScreen,
    fullscreenWidth,
    fullscreenHeight,
    defaultWidth,
    defaultHeight,
    setDimensions,
    positionStorage,
    onVisibilityChange,
    resolveTogglePosition,
  ]);

  /** Closes the window and exits fullscreen. */
  const close = React.useCallback(() => {
    setIsFullScreen(false);
    onClose();
    onVisibilityChange?.(false);
  }, [onClose, onVisibilityChange]);

  // Commit position adjustment when resize ends
  const prevIsResizing = React.useRef(isResizing);
  React.useEffect(() => {
    if (prevIsResizing.current && !isResizing) {
      if (positionAdjustment.deltaX !== 0 || positionAdjustment.deltaY !== 0) {
        const newPos = clampPosition({
          x: position.x + positionAdjustment.deltaX,
          y: position.y + positionAdjustment.deltaY,
        });
        setPosition(newPos);
        positionStorage.save(newPos);
        clearPositionAdjustment();
      }
    }
    prevIsResizing.current = isResizing;
  }, [isResizing, position, positionAdjustment, clampPosition, clearPositionAdjustment, positionStorage]);

  // dnd-kit sensor configuration
  const desktopSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );
  const mobileSensors = useSensors();
  const sensors = isMobile ? mobileSensors : desktopSensors;

  /** Commits the final position after a drag ends. */
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { delta } = event;
      setIsDragging(false);

      const newX = position.x + delta.x;
      const newY = position.y + toBottomPositionDelta({ screenDeltaY: delta.y });

      const clampedPos = clampPosition({ x: newX, y: newY });
      setPosition(clampedPos);
      positionStorage.save(clampedPos);
    },
    [position, clampPosition, positionStorage]
  );

  /** Marks drag as active. */
  const handleDragStart = React.useCallback(() => {
    setIsDragging(true);
  }, []);

  // Preserves the pre-existing contract for consumers that don't opt in to
  // staying mounted (e.g. PdfWindow): unmount for real on hide, so external
  // resources held by children (pdf.js documents, canvases, blob URLs)
  // don't outlive the window being closed.
  if (!keepMountedWhenHidden && !isVisible && !isFullScreen) {
    return null;
  }

  const internals: WindowInternals = {
    position,
    resizableWidth,
    resizableHeight,
    isVisible,
    isFullScreen,
    isDragging,
    isResizing,
    positionAdjustment,
    dndId,
    frameTestId,
    isMobile,
    close,
    toggleFullScreen,
    startResize,
    className,
    onClickOutside,
    frameZIndex,
    overlayZIndex,
  };

  return (
    <WindowInternalsContext.Provider value={internals}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToWindowEdges]}
      >
        <WindowFrameInner>{children}</WindowFrameInner>
      </DndContext>
    </WindowInternalsContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

/**
 * Generic drag-handle header for WidgetWindow.
 *
 * Wraps children in a CardHeader that acts as the drag activator area.
 * Applies cursor-grab / cursor-grabbing classes based on drag state.
 */
function WidgetWindowHeader({ children, className }: WidgetWindowHeaderProps) {
  const { isDragging, dragHandleProps } = useWidgetWindow();

  return (
    <CardHeader
      ref={dragHandleProps.ref}
      className={cn(
        "flex flex-row items-center gap-2 px-4 py-3",
        isDragging ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing",
        className
      )}
      {...dragHandleProps.listeners}
    >
      {children}
    </CardHeader>
  );
}

// ---------------------------------------------------------------------------
// CloseButton
// ---------------------------------------------------------------------------

/**
 * Reusable close button for WidgetWindow.
 *
 * Renders an icon button with the X icon that calls close() from context.
 * Styled consistently with the existing widget close button.
 */
function WidgetWindowCloseButton() {
  const { close } = useWidgetWindow();

  return (
    <Button
      size="icon"
      variant="ghost"
      data-testid="widget-window-close-button"
      aria-label="Close"
      onClick={close}
    >
      <X className="h-4 w-4" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// FullScreenToggle
// ---------------------------------------------------------------------------

/**
 * Reusable fullscreen toggle button for WidgetWindow.
 *
 * Renders Maximize2 or Minimize2 icon based on current fullscreen state.
 * Styled consistently with the existing widget fullscreen toggle.
 */
function WidgetWindowFullScreenToggle() {
  const { isFullScreen, toggleFullScreen } = useWidgetWindow();

  return (
    <Button
      size="icon"
      variant="ghost"
      data-testid="widget-window-fullscreen-toggle"
      aria-label={isFullScreen ? "Minimize" : "Maximize"}
      onClick={() => toggleFullScreen()}
    >
      {isFullScreen ? (
        <Minimize2 className="h-4 w-4" />
      ) : (
        <Maximize2 className="h-4 w-4" />
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Namespace export
// ---------------------------------------------------------------------------

/**
 * WidgetWindow namespace providing a composable floating window abstraction.
 *
 * Usage:
 * ```tsx
 * <WidgetWindow.Provider storageKeyPrefix="pdf" isVisible={true} onClose={close} dndId="pdf-window">
 *   <WidgetWindow.Header>
 *     <span>Title</span>
 *     <WidgetWindow.FullScreenToggle />
 *     <WidgetWindow.CloseButton />
 *   </WidgetWindow.Header>
 *   <YourContent />
 * </WidgetWindow.Provider>
 * ```
 */
export const WidgetWindow = {
  Provider: WidgetWindowProvider,
  Context: WidgetWindowContext,
  Header: WidgetWindowHeader,
  CloseButton: WidgetWindowCloseButton,
  FullScreenToggle: WidgetWindowFullScreenToggle,
};

export type {
  WidgetWindowContextValue,
  WidgetWindowProviderProps,
  WidgetWindowHeaderProps,
  DragHandleProps as WidgetWindowDragHandleProps,
};
